import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { packageTypeService } from '../../services/cityService';

const PackageTypeForm = ({ packageType, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      description: ''
    }
  });

  useEffect(() => {
    if (packageType) {
      setValue('name', packageType.name);
      setValue('description', packageType.description || '');
    }
  }, [packageType, setValue]);

  const onSubmit = async (data) => {
    if (!data.name.trim()) {
      toast.error('Package type name is required');
      return;
    }

    setLoading(true);
    try {
      const packageTypeData = {
        name: data.name.trim(),
        description: data.description.trim()
      };

      if (packageType) {
        await packageTypeService.updatePackageType(packageType.id, packageTypeData);
        toast.success('Package type updated successfully');
      } else {
        await packageTypeService.createPackageType(packageTypeData);
        toast.success('Package type created successfully');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving package type:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show more specific error message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save package type';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {packageType ? 'Edit Package Type' : 'Add New Package Type'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Package Type Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Type Name *
            </label>
            <input
              type="text"
              {...register('name', { 
                required: 'Package type name is required',
                minLength: {
                  value: 2,
                  message: 'Package type name must be at least 2 characters'
                },
                maxLength: {
                  value: 50,
                  message: 'Package type name must not exceed 50 characters'
                }
              })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter package type name (e.g., Standard, Media, E-commerce)"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              This will be displayed to users when selecting a package type.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              {...register('description', {
                maxLength: {
                  value: 500,
                  message: 'Description must not exceed 500 characters'
                }
              })}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe what types of businesses this package type is suitable for..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
            <div className="mt-1 flex justify-between text-sm text-gray-500">
              <span>Optional: Provide a brief description of this package type</span>
              <span>{watch('description')?.length || 0}/500</span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (packageType ? 'Update Package Type' : 'Create Package Type')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PackageTypeForm; 